import React, { useEffect, useState, Fragment }  from 'react';
import { connect } from 'react-redux';
import { Button, Icon, Popup } from 'semantic-ui-react'
import InfiniteScroll from 'react-infinite-scroll-component';

import useApiService from '../../services/useApiService';
import { debounce, getText } from '../../helpers';

import SemanticUiModal from '../../components/modal';
import ElementLoader from '../../components/element_loader';
import RisksComparisonMenu from './risks_comparison_menu';
import LibraryControlItem from '../../components/controls_component/library_control_item';

import './risks_comparison.css'

const ControlTypeComponent = ({controlType}) => {
  const abbre = controlType.includes("preventative") ? 'P' : 'R'
  return (
    <Popup
      trigger={ <span className='control-type-icon'>{abbre}</span> }
      content={
        <Fragment>
          <div><span className='control-type-icon'>P</span><span> - </span><span>{getText("Preventative Control")}</span></div>
          <div><span className='control-type-icon'>R</span><span> - </span><span>{getText("Recovery Control")}</span></div>
        </Fragment>
      }
      className = "control-type-icon-popup"
      horizontalOffset={14}
    />
  )
}

function RisksComparisonItem(props) {
  const { control, plansForComparison, translations, riskDescription, planId, riskId } = props;
  const [{ isLoading: isCreatingControl, response: createdControl, error }, createControl] = useApiService({ url: `/risk/${riskId}/create_control`, method: "post", defaultResponse: null });
  const isCreateControlSuccess = createdControl && !error;

  const onCreateControl = () => {
    const { name, control_type, library_control_id, tag_list } = control;
    const controlDetails = {
      plan_id: planId,
      risk_id: riskId,
      control_type,
      name,
      library_control_id,
      tag_list
    }
    createControl(controlDetails);
  }

  const findRiskByDescription = (risks, riskDescription) => {
    return risks.find((risk)=>{
      return `${risk.work_phase} / ${risk.activity} / ${risk.hazard} / ${risk.unwanted_event}` === riskDescription
    })
  }

  const isControlInRisk = (control, risk) => {
    if (!risk) return false;

    const { library_control_id, control_type, name } = control
    if (library_control_id) {
      return risk.controls.find((_control) => { return _control.library_control_id === library_control_id && _control.control_type === control_type })
    } else {
      return risk.controls.find((_control) => { return _control.name === name && _control.control_type === control_type })
    }
  }

  const identifyPlans = () => {
    let assignedPlans = [], currentPlan = null, riskMatch;
    
    for (const plan of plansForComparison) {
      riskMatch = findRiskByDescription(plan.risks, riskDescription);

      if (!isControlInRisk(control, riskMatch)) continue;

      if (plan.id === planId) {
        currentPlan = plan;
      } else {
        assignedPlans.push(plan)
      }
    }

    return { assignedPlans, currentPlan }
  }

  const { assignedPlans, currentPlan } = identifyPlans();
  const canAssignControlToCurrentPlan = (!currentPlan && !isCreatingControl && !isCreateControlSuccess) || error;

  return (
    <tr key={control.id} className="risks-comparison-item">
      <td className="control-id">{control.id}</td>
      <td>
        {
          control.library_control_id ?
          <LibraryControlItem translations={translations} control={control.library_control} showDeleteButton={false}/> :
          <span>
            <div>{control.name}</div>
            {
              control.tag_list &&
              <div>
                {
                  control.tag_list.map(tag=>{
                    return <span className='ui label custom-control-tag' value={tag}>{tag}</span>
                  })
                }
              </div>
            }
          </span>
        }
        <div className="plan-assignment">
          <label>{getText("Control used in")}:</label>
          <Popup
            trigger={ <sup><Icon name='question circle outline' /></sup> }
            content={ <span>{getText("This control is utilized in the following plans. Colored green means it is in the current plan.")}</span> }
            className = "control-used-in-popup"
            horizontalOffset={14}
          />
          {
            currentPlan &&
            <Fragment>
              <span className="plan-assigned current-plan">{currentPlan.name}</span>
              (<ControlTypeComponent controlType={control.control_type} />)
              { assignedPlans.length > 0 && <span>, </span> }
            </Fragment>
          }
          {
            assignedPlans.map(({name}, index)=>{
              return (
                <Fragment>
                  <span className="plan-assigned">{name}</span>
                  (<ControlTypeComponent controlType={control.control_type} />)
                  { index !== assignedPlans.length - 1 && <span>, </span> }
                </Fragment>
              )
            })
          }
        </div>
        { 
          canAssignControlToCurrentPlan  &&
          <div class="link" onClick={onCreateControl}>{getText("Assign to current plan")}</div>
        }
        {
          isCreatingControl && 
          <div className='inline-loading-message'>
            <span>{getText("Assigning control to plan")}..</span>
            <i className="loading circle notched icon"></i>
          </div> 
        }
        { 
          isCreateControlSuccess && 
          <div className='success-message'>
            <Icon name='check'/>
            <span>{getText("Successfully assigned this control to the current plan. You need to re-open this view to refresh this control.")}</span>
          </div>
        }  
        {
          error &&
          <div className='error-message'>
            <span>{getText("Error occurred while assigning this control to the current plan. Please try again.")}</span>
          </div>
        }
      </td>
    </tr>
  )
}

function RisksComparisonHeader() {
  return (
    <div>
      {getText("Compare controls for specific risk in different plans")}
    </div>
  )
}

function RisksComparisonContent(props) {
  const { planId, translations, riskDescription, riskId, planName } = props;

  const default_state = {
    page: 1,
    per_page: 20,
    search_term: '',
    custom_control_tags_filter: [],
    lib_control_tags_filter: [],
    lib_control_admins_filter: [],
    with_unique_details: true,
    risk_description: riskDescription,
    plan_ids: []
  }

  const [ state, setState ] = useState(default_state)
  const [ controls, setControls ] = useState([]);
  const [ controlsTimer, setControlsTimer] = useState();
 
  const [{ isLoading: isPlanDropdownOptionsLoading, response: planDropdownOptions }, getPlanDropdownOptions] = useApiService({ url: `/plans`, defaultResponse: [] });
  const [{ isLoading: isPlansForComparisonLoading, response: plansForComparison }, getPlansForComparison] = useApiService({ url: `/plans`, defaultResponse: [] });
  const [{ isLoading: isControlsLoading, response: fetchedControls }, getControls] = useApiService({ url: `/controls`, onSuccess: onGetControlsSuccess, defaultResponse: [] });

  function onGetControlsSuccess(fetchedControls) {
    if (state.page === 1) return setControls(fetchedControls);
    setControls(controls.concat(fetchedControls));
  }

  const setNewPage = () => {
    if (fetchedControls.length < state.per_page) return;
    setState({ ...state, page: state.page + 1 })
  }

  const onPlanDropdownChange = (_, data) => {
    setState({ ...state, page: 1, plan_ids: data.value })
  }

  const onSearchChange = (_, data) => {
    setState({ ...state, page: 1, search_term: data.value })
  }

  const onTagsChange = (data) => {
    const { 
      selectedControlTags: custom_control_tags_filter,
      selectedLibControlTags: lib_control_tags_filter,
      selectedLibControlAdmins: lib_control_admins_filter,
    } = data;
    setState({ ...state, page: 1, custom_control_tags_filter, lib_control_tags_filter, lib_control_admins_filter })
  }

  const debouncedGetControls = () => {
    let newControlsTimer = debounce(getControls, 1000, controlsTimer)({ params: state });
    setControlsTimer(newControlsTimer)
  }

  useEffect(()=>{
    debouncedGetControls()
  }, [state.page, state.search_term, state.plan_ids, state.custom_control_tags_filter, state.lib_control_tags_filter, state.lib_control_admins_filter])

  useEffect(()=>{
    const plan_options_params = {
      is_template: false,
      disregard_permissions: true,
      return_format: 'plans_for_dropdown_options'
    }
    getPlanDropdownOptions({ params: plan_options_params });
  }, [])

  useEffect(()=>{
    const plansForComparisonParams = {
      is_template: false,
      plan_ids: state.plan_ids,
      disregard_permissions: true,
      return_format: 'plans_for_risks_comparison',
      risk_description: riskDescription,
    }
    getPlansForComparison({ params: plansForComparisonParams });
  }, [state.plan_ids])

  return (
    <Fragment>
      <div className='plan-name-wrapper'>
        <label>{getText("Current Plan")}:</label>
        <span className='plan-name'>{planName}</span>
      </div>
      <div className='risk-description-wrapper'>
        <label>{getText("Risk Description")}:</label>
        <span className='risk-description'>{riskDescription}</span>
      </div>
      <RisksComparisonMenu
        planDropdownOptions={planDropdownOptions}
        selectedPlanIds={state.plan_ids}
        searchTerm={state.search_term}
        onPlanDropdownChange={onPlanDropdownChange}
        onSearchChange={onSearchChange}
        onTagsChange={onTagsChange}
        showFilterByPlans={true}
        showFilterByControlTags={true}
      />
      <InfiniteScroll
        dataLength={controls.length}
        next={setNewPage}
        hasMore={true}
        height={1}
      >
        <table className="risks-comparison-table table">
          <thead>
            <tr>
              <th className="control-id-header">{getText("Control ID")}</th>
              <th className="control-details-header">{getText("Controls for this risk in different plans")}</th>
            </tr>
          </thead>
          {
            controls && controls.length ?
            <tbody>
              {
                controls.map((control, index)=>{
                  return (
                    <RisksComparisonItem
                      planId={planId}
                      riskId={riskId}
                      control={control}
                      plansForComparison={plansForComparison}
                      translations={translations}
                      riskDescription={riskDescription}
                    />
                  )
                })
              } 
            </tbody>
            :
            <tbody>
              <tr>
                <td colspan="3" className='no-records'>{getText("No records found.")}</td>
              </tr>
            </tbody>
          }
          <ElementLoader isLoading={isControlsLoading} message={`${getText("Fetching controls")}...`} />
          <ElementLoader isLoading={isPlansForComparisonLoading} message={`${getText("Fetching selected plans for comparison")}...`} />
          <ElementLoader isLoading={isPlanDropdownOptionsLoading} message={`${getText("Fetching all available plans")}...`} />
        </table>
      </InfiniteScroll>
    </Fragment>
  )
}

function RisksComparisonActions(props) {
  return (
    <div>
      <Button fluid size='medium' className="new-plan-button" onClick={props.hideRisksComparison}>
        {getText("Close")}
      </Button>
    </div>
  )
}

function RisksComparison(props) {
  const { risk, hideRisksComparison, translations, plan } = props;
  const { name: planName } = plan || {};
  const riskDescription = risk ? `${risk.work_phase_or_ambient_exposure} / ${risk.activity} / ${risk.hazard} / ${risk.unwanted_event}` : '';
  const modalOptions = { className: "risks-comparison", size: "large" };

  return (
    <SemanticUiModal
      modalOptions={modalOptions}
      header={<RisksComparisonHeader />}
      content={
        <RisksComparisonContent
          planId={risk.plan_id}
          riskId={risk.id}
          translations={translations}
          planName={planName} 
          riskDescription={riskDescription}
        />
      }
      actions={<RisksComparisonActions hideRisksComparison={hideRisksComparison}/>}
      closeModal={hideRisksComparison}
    />
  )
}

const mapStateToProps = (state) => {
  return {
    translations: state.language.language.translations,
    plan: state.plan.plan,
  }
}

export default connect(mapStateToProps, {})(RisksComparison);



// WEBPACK FOOTER //
// ./src/views/risks_comparison/risks_comparison.js